import { useEffect } from "react";
import { Outlet, useParams, useNavigate, Link, NavLink, useLocation } from "react-router-dom";
import { Home, CalendarDays, User, Search, ArrowLeft } from "lucide-react";
import TenantLogo from "@/components/TenantLogo";
import { useTenant } from "./TenantRoot";

const TYPING_FIELDS = 'input:not([type="checkbox"]):not([type="radio"]):not([type="date"]):not([type="file"]), textarea';

export { useTenant };

/**
 * The customer app frame.
 * Phones and the native apps: a back bar on inner pages and a bottom tab bar.
 * Tablets and desktops: a top navigation bar and a wide content column.
 */
export default function TenantShell() {
  const { slug } = useParams();
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const location = useLocation();

  const base = `/t/${slug}`;
  const appName = tenant.branding?.app_name || tenant.business_name;
  const isHome = location.pathname === base || location.pathname === `${base}/`;

  const tabs = [
    { to: base, icon: Home, label: "Home", end: true, testid: "tenant-tab-home" },
    { to: `${base}/search`, icon: Search, label: "Search", testid: "tenant-tab-search" },
    { to: `${base}/bookings`, icon: CalendarDays, label: "Bookings", testid: "tenant-tab-bookings" },
    { to: `${base}/account`, icon: User, label: "Account", testid: "tenant-tab-account" },
  ];

  // While a text field has focus the on-screen keyboard is up. The tab bar is
  // hidden then (see brand.css) so it neither covers the form nor gets tapped by mistake.
  // It comes back a moment after the field loses focus, not at once: the tap that
  // moved focus (a form's own button) is still in progress, and a tab bar appearing
  // under the finger would take that tap instead.
  useEffect(() => {
    const root = document.documentElement;
    let restore = null;
    const onFocusIn = (e) => {
      if (!e.target.matches?.(TYPING_FIELDS)) return;
      clearTimeout(restore);
      root.classList.add("keyboard-open");
    };
    const onFocusOut = () => {
      clearTimeout(restore);
      restore = setTimeout(() => root.classList.remove("keyboard-open"), 400);
    };
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    return () => {
      clearTimeout(restore);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
      root.classList.remove("keyboard-open");
    };
  }, []);

  // Go back within the app; a page opened directly (deep link) goes to this tenant's home.
  const goBack = () => (window.history.state?.idx > 0 ? navigate(-1) : navigate(base));

  return (
    <div className="pwa-shell brand-body" data-testid="tenant-shell">
      <header className="top-nav" data-testid="tenant-top-nav">
        <div className="pwa-container px-6 h-16 flex items-center gap-6">
          <Link to={base} className="flex items-center gap-3 min-w-0">
            <TenantLogo tenant={tenant} />
            <span className="brand-heading font-bold text-lg truncate">{appName}</span>
          </Link>
          <nav className="ml-auto flex items-center gap-1" aria-label="Main">
            {tabs.map((t) => (
              <NavLink key={t.to} to={t.to} end={t.end}
                className="px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2"
                style={({ isActive }) => ({
                  color: isActive ? "hsl(var(--brand-primary))" : "hsl(var(--brand-text-muted))",
                  background: isActive ? "hsl(var(--brand-primary) / 0.1)" : "transparent",
                })}>
                <t.icon className="h-4 w-4" /> {t.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </header>

      {!isHome && (
        <header className="mobile-only sticky top-0 z-40 brand-surface border-b brand-border h-14 px-1 flex items-center justify-between">
          <button onClick={goBack} className="h-11 px-3 inline-flex items-center gap-1.5 text-sm font-semibold brand-text" data-testid="tenant-back-btn">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <div className="font-bold text-sm brand-heading truncate px-2">{appName}</div>
          <div className="w-16 flex-shrink-0" />
        </header>
      )}

      <main className="pwa-container">
        <Outlet />
      </main>

      <nav className="bottom-nav" aria-label="Main">
        {tabs.map((t) => (
          <NavLink key={t.to} to={t.to} end={t.end} data-testid={t.testid}
            style={({ isActive }) => ({ color: isActive ? "hsl(var(--brand-primary))" : "hsl(var(--brand-text-muted))" })}>
            <t.icon className="h-5 w-5" />
            <span className="text-[10px] font-semibold">{t.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
